/**
 * Hook to set the HTML document title for the current page.
 *
 * Format: "<App>: <Page>" (e.g. "AI Lab: Updates"), or the app name alone when there is no page. Off production the
 * environment from /me goes first (e.g. "STAGING AI Lab: Updates"), so a staging tab can't pass for production.
 */

import { useEffect } from "react";
import type { Environment } from "../api/users";
import { APP_NAME } from "../appName";
import { useMe } from "./useUsers";

/** The document title of [page], with [environment] in front of it unless that is production or not known yet. */
export function pageTitle(page?: string, environment?: Environment): string {
  const title = page ? `${APP_NAME}: ${page}` : APP_NAME;
  return environment && environment !== "PRODUCTION"
    ? `${environment} ${title}`
    : title;
}

/**
 * Sets document.title whenever the page or the environment changes. It reads /me from the query cache and never
 * fetches it: signed out there is no /me, and the title carries no environment.
 *
 * @param page - The page's own name, without the app name, e.g. "Updates"
 */
export function usePageTitle(page?: string): void {
  const { data: me } = useMe({ enabled: false });
  const title = pageTitle(page, me?.environment);
  useEffect(() => {
    document.title = title;
  }, [title]);
}
